import { useAuth0 } from "@auth0/auth0-react"
import { createContext, useContext, useEffect, useMemo, useState } from "react"
import { useLocation, useParams } from "react-router-dom"
import { Icon, Popup } from "semantic-ui-react"
import BatteryIcon from "../../../../../Components/BifrostElementComponent/BatteryIcon"
import BifrostTab from "../../../../../Components/BifrostElementComponent/BifrostTab/BifrostTab"
import InterfaceIcon from "../../../../../Components/BifrostElementComponent/InterfaceIcon"
import DeviceDetailsComponent from "../../../../../Components/BifrostPageComponent/DeviceComponents/DeviceDetailsComponent/DeviceDetailsComponent"
import Loader from "../../../../../Components/BifrostPageComponent/Loader/Loader"
import BifrostAuditComponent from "../../../../../Components/BifrostPageComponent/OrganizationComponents/BifrostAuditComponent"
import BifrostEventComponent from "../../../../../Components/BifrostPageComponent/OrganizationComponents/BifrostEventComponent"
import BifrostSessionComponent from "../../../../../Components/BifrostPageComponent/OrganizationComponents/BifrostSessionComponent"
import { useGetDeviceById } from "../../../../../Hooks/FetchHooks/GetDataService/useGetDataById/useGetDeviceById"
import { useGetLatestFirmwareVersion } from "../../../../../Hooks/FetchHooks/GetDataService/useGetLatestFirmwareVersion"
import userInfoContext from "../../../../../store/userInfoContext"
import { hasFeatureInPackage, isSuperAdminOrResellerAdminOrAdmin, isVersionHigherOrEqual, ledColor } from "../../../../../Utils/util"
import Error401 from "../../../../OtherPages/ErrorPage/Error401"
import MainPanelFooter from "../../../MainPanelFooter/MainPanelFooter"
import MainPanelHeader from "../../../MainPanelHeader/MainPanelHeader"

export const DeviceContext = createContext({
	deviceDetails: {
		deviceDetailsData: {},
		getDeviceDetails: () => {}
	}
})

const DeviceDetailsPage = () => {
	const { id } = useParams()
	const { state } = useLocation()
	const userInfo = useContext(userInfoContext)
	const { user } = useAuth0()
	const { isLoading: deviceIsLoading, data, isError, getDeviceById } = useGetDeviceById(id)
	const { latestFirmwareVersion, getLatestFirmwareVersion } = useGetLatestFirmwareVersion(id)
	const [firmwareUpToDate, setFirmwareUpToDate] = useState(true)
	const [name, setName] = useState(data ? data.shortname : "")

	const detailData = useMemo(() => {
		return [
			{
				tabName: "Details",
				feature: null,
				featureName: null,
				featureIcon: null,
				renderCondition: null,
				disabled: false,
				tabContent: (
					<DeviceDetailsComponent
						data={data}
						deviceIsLoading={deviceIsLoading}
						getDeviceById={getDeviceById}
						latestFirmwareVersion={latestFirmwareVersion}
						firmwareUpToDate={firmwareUpToDate}
					/>
				)
			},
			{
				tabName: "Audit",
				feature: hasFeatureInPackage(userInfo, "audit_log"),
				featureName: "audit_log",
				featureIcon: "history",
				disabled: !isSuperAdminOrResellerAdminOrAdmin(user),
				tabContent: <BifrostAuditComponent chosenItem={data} chosenItemType="device" />
			},
			{
				tabName: "Events",
				feature: hasFeatureInPackage(userInfo, "audit_log"),
				featureName: "audit_log",
				featureIcon: "calendar check",
				disabled: !isSuperAdminOrResellerAdminOrAdmin(user),
				tabContent: <BifrostEventComponent chosenItem={data} chosenItemType="device" />
			},
			{
				tabName: "Sessions",
				feature: hasFeatureInPackage(userInfo, "statistics"),
				featureName: "statistics",
				featureIcon: "list",
				disabled: !isSuperAdminOrResellerAdminOrAdmin(user),
				tabContent: <BifrostSessionComponent chosenItem={data} chosenItemType="device" />
			}
		]
	}, [data, deviceIsLoading, latestFirmwareVersion, firmwareUpToDate])

	const style = {
		tabular: true,
		tabStyle: { paddingTop: "10px" },
		contentStyle: { padding: "20px" }
	}

	useEffect(() => {
		if (data.firmware && latestFirmwareVersion) {
			setFirmwareUpToDate(isVersionHigherOrEqual(data.firmware, latestFirmwareVersion))
		}
	}, [latestFirmwareVersion, data])

	useEffect(() => {
		setName(
			data.shortname ? (
				<>
					{data.shortname}
					<div style={{ marginLeft: "10px", display: "flex", alignItems: "center" }}>
						<Popup
							basic
							trigger={<Icon name="circle" size="small" style={{ marginRight: "10px" }} color={ledColor(data.state.toLowerCase())} />}
							content={data.state.toUpperCase()}
							size="mini"
							style={{ fontWeight: "bold", backgroundColor: "#efefef" }}
						></Popup>
						{data.activeIface && (
							<Popup
								basic
								trigger={
									<div style={{ display: "flex", alignItems: "center" }}>
										<InterfaceIcon size="tiny" data={data} />
									</div>
								}
								content={data.activeIface.toUpperCase()}
								size="mini"
								style={{ fontWeight: "bold", backgroundColor: "#efefef" }}
							></Popup>
						)}
						{data.battery && (
							<Popup
								basic
								trigger={
									<div style={{ display: "flex", alignItems: "center" }}>
										<BatteryIcon size="small" data={data} />
									</div>
								}
								content={data.battery.levelP + "%"}
								size="mini"
								style={{ fontWeight: "bold", backgroundColor: "#efefef" }}
							></Popup>
						)}
					</div>
				</>
			) : (
				data.name
			)
		)
	}, [data])

	useEffect(() => {
		if (userInfo && userInfo.terms_accepted) {
			getDeviceById()
			getLatestFirmwareVersion()
		}
	}, [id, userInfo])

	return (
		<>
			<DeviceContext.Provider value={{ deviceDetails: { deviceDetailsData: data, getDeviceDetails: getDeviceById } }}>
				<section className={"bifrost-table-panel devices-table"}>
					<MainPanelHeader title={isError ? " " : "Devices"} name={deviceIsLoading ? (state ? state.name : "") : name} navPath={"/devices"} />
					{deviceIsLoading ? (
						<Loader backgroundColor="white" inverted height="1px" minHeight="200px" />
					) : isError ? (
						<Error401 backgroundColor="white" />
					) : (
						<BifrostTab detailData={detailData} style={style} />
					)}
					<MainPanelFooter />
				</section>
			</DeviceContext.Provider>
		</>
	)
}

export default DeviceDetailsPage
